import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Text, Pressable, ScrollView,
} from 'react-native';
import Style from '../../../../../../constants/Style';
import Colors from '../../../../../../constants/Colors';
import Switch from '../../../../../Switch';
import PriorityFlag from '../../../../../16pxIcons/PriorityFlag';
import DropdownIcon from '../../../../../16pxIcons/DropdownIcon';
import QuestionIcon from '../../../../../24pxIcons/QuestionIcon';
import UserIcon24 from '../../../../../24pxIcons/UserIcon24';
import DropdownIcon12 from '../../../../../12pxIcons/DropdownIcon12';
import CloseIconSmall from '../../../../../12pxIcons/CloseIconSmall';
import isMobile from '../../../../../IsMobile';
import getPriorityColor from '../../../../../PriorityColorScheme';
import SelectionModal from '../../../../../SelectionModal';
import { UserGroup } from '../../../../../../constants/Types';
import Option from './IssueRow/components/Option';
import PressableStyled from '../../../../../PressableStyled';
import AssignUserGroupButton from './IssueRow/components/AssignUserGroupButton';

const otherIssueHelpText: string = 'Switching this on lets the user report a miscellaneous issue, where they can specify exactly what\'s wrong';

interface OtherIssueRowProps {
  otherIssueEnabled: boolean,
  priorityDropdownShownIndex: number,
  issueTagDropdownShownIndex: number,
  categoryDropdownShownIndex: number,
  otherIssuePriority: number,
  userGroups: UserGroup[],
  otherIssueTags: string[],
  setOtherIssueTags: Dispatch<SetStateAction<string[]>>,
  setPriorityDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  setIssueTagDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  setOtherIssueEnabled: Dispatch<SetStateAction<boolean>>,
  setOtherIssuePriority: Dispatch<SetStateAction<number>>,
  setCategoryDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  slaNames: string[],
}

const OtherIssueRow = ({
  otherIssueEnabled, otherIssuePriority, userGroups, priorityDropdownShownIndex, otherIssueTags,
  setOtherIssuePriority, setOtherIssueEnabled, setOtherIssueTags, setPriorityDropdownShownIndex,
  setIssueTagDropdownShownIndex, categoryDropdownShownIndex, setCategoryDropdownShownIndex,
  slaNames, issueTagDropdownShownIndex,
}: OtherIssueRowProps) => {
  const [otherIssueHelpShown, setOtherIssueHelpShown] = useState<boolean>(false);
  const [tagDropdownShownIndex, setTagDropdownShownIndex] = useState<number>(-1);

  const userGroupsInvalid: boolean = !(otherIssueTags?.length > 0);

  useEffect(() => {
    if (!(issueTagDropdownShownIndex > -1 || priorityDropdownShownIndex > -1
      || categoryDropdownShownIndex > -1)) return;
    setOtherIssueHelpShown(false);
  }, [issueTagDropdownShownIndex, priorityDropdownShownIndex, categoryDropdownShownIndex]);

  const hideDropdowns: () => void = () => {
    setPriorityDropdownShownIndex(null);
    setTagDropdownShownIndex(-1);
    setIssueTagDropdownShownIndex(null);
    setCategoryDropdownShownIndex(null);
  };

  const toggleHelpBoxVisibility = () => {
    hideDropdowns();
    if (otherIssueHelpShown) setOtherIssueHelpShown(false);
    else {
      const timeout1 = setTimeout(() => {
        setOtherIssueHelpShown(true);
        clearTimeout(timeout1);
      }, 100);
      const timeout2 = setTimeout(() => {
        setOtherIssueHelpShown(false);
        clearTimeout(timeout2);
      }, 600 + 400 * otherIssueHelpText.split(' ').length);
    }
  };

  const PriorityList = () => <>
    {slaNames.map((text: string, index: number) => <Option
      key={`priority_option_${text}`}
      text={`P${index + 1} - ${text}`}
      selected={otherIssuePriority === index}
      onPress={() => setOtherIssuePriority(index)}
    />)}
  </>;

  const AddGroupList = () => <View testID='select-issue-user-groups-list'>
    {remainingUserGroups?.map((group: UserGroup) => <Option
      key={`new_user_group_option_${group.UserGroupId}`}
      text={group.Name}
      selected={otherIssueTags?.length
        && group.UserGroupId === otherIssueTags[otherIssueTags?.length]}
      onPress={() => {
        const updatedTags: string[] = otherIssueTags ?? [];
        updatedTags.push(group.UserGroupId);
        setOtherIssueTags(updatedTags);
        hideDropdowns();
      }}
    />)}
  </View>;

  const remainingUserGroups: UserGroup[] = userGroups.filter((group: UserGroup) => !otherIssueTags
    ?.includes(group.UserGroupId));

  const addTagIconDisabled: boolean = !(remainingUserGroups?.length > 0);

  const priorityDropdownShown: boolean = priorityDropdownShownIndex === -1;
  const issueSelected: boolean = issueTagDropdownShownIndex === -1;
  const addTagDropdownShown: boolean = issueTagDropdownShownIndex === -1
    && tagDropdownShownIndex === (otherIssueTags?.length ?? 0);

  const addTagPress: () => void = () => {
    setPriorityDropdownShownIndex(null);
    setIssueTagDropdownShownIndex((issueSelected && addTagDropdownShown) ? null : -1);
    setTagDropdownShownIndex((issueSelected && addTagDropdownShown)
      ? -1 : (otherIssueTags?.length ?? 0));
    setCategoryDropdownShownIndex(null);
  };

  const togglePriorityDropdown: () => void = () => {
    setPriorityDropdownShownIndex(priorityDropdownShown ? null : -1);
    setTagDropdownShownIndex(-1);
    setIssueTagDropdownShownIndex(null);
    setCategoryDropdownShownIndex(null);
  };

  return <View style={[styles.container, otherIssueHelpShown && { zIndex: 3 }]}>
    <View style={[Style.row, { zIndex: 2 }]}>
      <View style={styles.otherIssueSettings}>
        <View style={Style.row}>
          <View style={Style.row}>
            <Switch
              toggled={otherIssueEnabled}
              toggle={() => setOtherIssueEnabled(!otherIssueEnabled)}
              testID='other-issue-type-toggle-button'
            />
            <Text style={styles.otherText}>{'Other'}</Text>
          </View>
          {otherIssueEnabled && <Pressable
            onPress={togglePriorityDropdown}
            style={[
              styles.priorityContainer, Style.row,
              { borderColor: getPriorityColor(otherIssuePriority) },
            ]}
          >
            <PriorityFlag color={getPriorityColor(otherIssuePriority)} />
            <Text style={[styles.priorityText, { color: getPriorityColor(otherIssuePriority) }]}>
              {`P${otherIssuePriority + 1}`}
            </Text>
            <DropdownIcon
              color={getPriorityColor(otherIssuePriority)}
              style={priorityDropdownShown ? Style.inverted : Style.upright}
            />
          </Pressable>}
        </View>
        {isMobile ? <SelectionModal
          listContent={<PriorityList />}
          title='Choose priority'
          isVisible={priorityDropdownShown}
          hide={hideDropdowns}
        /> : (priorityDropdownShown && <View>
          <View style={[styles.dropdown, styles.priorityDropdown]}>
            <PriorityList />
          </View>
        </View>)}
      </View>
      <PressableStyled onPress={toggleHelpBoxVisibility}>
        <QuestionIcon />
      </PressableStyled>
      {otherIssueHelpShown && <View style={styles.otherIssueHelpBox}>
        <Text style={styles.otherIssueText}>{otherIssueHelpText}</Text>
      </View>}
    </View>
    {otherIssueEnabled && userGroups?.length ? <View style={styles.userGroupTagsContainer}>
      {(typeof otherIssueTags === 'object' && otherIssueTags?.length) ? otherIssueTags
        ?.map((tag: string, itemIndex: number) => {
          const tagSelected: boolean = issueSelected && tagDropdownShownIndex === itemIndex;
          const name: string = userGroups?.filter((group: UserGroup) => group.UserGroupId
            === tag)[0]?.Name;

          const removeTag: () => void = () => {
            hideDropdowns();
            const updatedTags = [...otherIssueTags];
            updatedTags.splice(itemIndex, 1);
            setOtherIssueTags(updatedTags);
          };

          const addTag: () => void = () => {
            setPriorityDropdownShownIndex(null);
            setIssueTagDropdownShownIndex(tagSelected ? null : -1);
            setTagDropdownShownIndex(tagSelected ? -1 : itemIndex);
            setCategoryDropdownShownIndex(null);
          };

          const ChangeGroupList = () => <View testID='select-issue-user-groups-list'>
            {remainingUserGroups?.map((group: UserGroup) => <Option
              text={group.Name}
              key={`other_issue_group_${group.UserGroupId}`}
              selected={otherIssueTags?.length
                && group.UserGroupId === otherIssueTags[itemIndex]}
              onPress={() => {
                const updatedTags: string[] = otherIssueTags ?? [];
                updatedTags[itemIndex] = group.UserGroupId;
                setOtherIssueTags(updatedTags);
                hideDropdowns();
              }}
            />)}
          </View>;

          return name && <View
            key={`tag_indicator_${itemIndex}`}
            style={styles.tagContainer}
          >
            <PressableStyled
              onPress={addTag}
              style={styles.tag}
              disabled={addTagIconDisabled}
              maintainOpacityWhenDisabled
            >
              <UserIcon24 />
              <Text style={styles.tagText}>{name}</Text>
              {!addTagIconDisabled && <DropdownIcon12
                style={tagSelected ? Style.inverted : Style.upright}
              />}
              <PressableStyled
                onPress={removeTag}
                hitSlop={{
                  top: 4, bottom: 4, left: 6, right: 6,
                }}
              >
                <CloseIconSmall />
              </PressableStyled>
            </PressableStyled>
            {isMobile ? <SelectionModal
              listContent={<ChangeGroupList />}
              title='Select user group'
              isVisible={tagSelected}
              hide={hideDropdowns}
            /> : (tagSelected && <View>
              <ScrollView
                nestedScrollEnabled
                style={styles.dropdown}
              >
                <ChangeGroupList />
              </ScrollView>
            </View>)}
          </View>;
        }) : null}
      <View>
        <AssignUserGroupButton
          onPress={addTagPress}
          disabled={addTagIconDisabled}
          invalid={userGroupsInvalid}
        />
        {isMobile ? <SelectionModal
          listContent={<AddGroupList />}
          title='Select user group'
          isVisible={addTagDropdownShown}
          hide={hideDropdowns}
        /> : (addTagDropdownShown && <View>
          <ScrollView
            style={styles.dropdown}
            nestedScrollEnabled
          >
            <AddGroupList />
          </ScrollView>
        </View>)}
      </View>
    </View> : null}
    {otherIssueEnabled && userGroups?.length > 0 && userGroupsInvalid && (
      <Text style={styles.errorText}>
        {'A user group must be assigned to an issue'}
      </Text>
    )}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 8,
    zIndex: -1,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 21,
    color: Colors.light.hereworksRed500,
    fontFamily: 'Roboto_400Regular',
    zIndex: -1,
  },
  otherText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack500,
    marginHorizontal: 8,
  },
  otherIssueHelpBox: {
    zIndex: 4,
    position: 'absolute',
    end: 28,
    bottom: 2,
    backgroundColor: '#FFF',
    width: 200,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    paddingHorizontal: 18,
    paddingVertical: 12,
  },
  otherIssueText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  otherIssueSettings: {
    flex: 1,
    marginEnd: 64,
    marginBottom: 2,
  },
  dropdown: {
    width: 170,
    maxHeight: 200,
    borderWidth: 2,
    borderRadius: 10,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    overflow: 'hidden',
    marginTop: 4,
    zIndex: 2,
    position: 'absolute',
  },
  priorityDropdown: {
    alignSelf: 'flex-end',
    position: 'absolute',
  },
  priorityContainer: {
    borderRadius: 4,
    borderWidth: 1,
    paddingHorizontal: 4,
    marginEnd: isMobile ? 0 : 8,
    backgroundColor: '#FFF',
  },
  priorityText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
    marginHorizontal: 2,
  },
  tagContainer: {
    alignSelf: 'flex-start',
    marginVertical: 2,
  },
  tag: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginEnd: 8,
  },
  tagText: {
    color: '#FFF',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 24,
    marginHorizontal: 4,
    flex: isMobile ? undefined : 1,
  },
  userGroupTagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
});

export default OtherIssueRow;
